/* The receipt drawer. Occupies the right of the grid rather than floating over it, so the figure
   you clicked stays visible beside its provenance — comparing the two is the point. */

.drawer {
  grid-column: 8 / 13;
  /* Row 2, not row 1. The KPI strip spans all twelve columns in row 1, so a drawer starting
     there displaces it — the figure you clicked would scroll out of view at the moment you
     asked where it came from, which defeats the point of a drawer over a modal. */
  grid-row: 2 / -1;
  /* **Above the screen, by declaration.** The drawer sits in grid cells the screen's own cards and
     tables also span, and it is meant to cover them. Chromium paints grid items atomically in
     document order, so it did, with no declaration. WebKit paints each tile's and table's positioned
     parts (sticky headers, anything with a transform) above a static sibling, so the drawer was
     painted *under* the Scope 2 tiles and a click on its first row landed on the "Estimates" tile
     (G-F, the browser gate in WebKit). A positioned box with a z-index above the table's (4) and
     below the shell header's (10) is the same answer in every engine. */
  position: relative;
  z-index: 5;
  background: var(--panel);
  border: 1px solid var(--accent-line);
  border-radius: var(--radius);
  box-shadow: var(--shadow-drawer);
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  /* Slides in over `--dur-3`: it was fully open by the first frame, which reads as a jump. Transform
     only, with no fade, so the text is at full contrast from the first frame it is drawn; the
     reduced-motion rule in tokens.css shortens it to nothing. A figure in it never animates. */
  animation: drawer-in var(--dur-3) var(--ease-out);
}

@keyframes drawer-in {
  from {
    transform: translateX(24px);
  }

  to {
    transform: translateX(0);
  }
}

.head {
  padding: 11px 13px;
  border-bottom: 1px solid var(--line);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  flex-shrink: 0;
}

/* An `h2`, so the drawer has an entry in a screen reader's heading list — it is the longest thing
   on the screen and the only one that appears on demand, and it had none. The browser's own
   heading margins and 1.5em size are reset here rather than the element being downgraded to a
   span, which is what it was. */
.headTitle {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
}

.headSub {
  display: block;
  color: var(--muted);
  font-size: var(--t-min);
}

.close {
  border: 1px solid var(--line);
  background: var(--panel);
  border-radius: 6px;
  width: 24px;
  height: 24px;
  cursor: pointer;
  color: var(--ink-2);
  flex-shrink: 0;
  line-height: 1;
}

.scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.figure {
  padding: 12px 13px 6px;
  font-size: 30px;
  font-weight: 500;
  letter-spacing: -0.03em;
  font-variant-numeric: lining-nums tabular-nums;
}

.figureUnit {
  font-size: var(--t-dense);
  color: var(--muted);
  margin-left: 5px;
  letter-spacing: 0;
}

/* The chain. Each step is one link from figure back to document. */
.chain {
  padding: 4px 13px 12px;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

/* What has been asked about the record, beneath the chain: not a link in it. */
.aside {
  border-top: 1px solid var(--line);
  padding: 8px 0;
  margin-top: 10px;
}

.asideLabel {
  margin: 0;
  font-size: var(--t-min);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
  font-weight: 700;
}

.stepBody {
  font-size: var(--t-dense);
  margin-top: 3px;
  overflow-wrap: anywhere;
}

.kv {
  display: grid;
  grid-template-columns: minmax(84px, auto) minmax(0, 1fr);
  gap: 2px 10px;
  font-size: var(--t-min);
  margin-top: 4px;
}

.k {
  color: var(--muted);
}

.v {
  color: var(--ink);
  min-width: 0;
  overflow-wrap: anywhere;
}

.mono {
  font-family: var(--mono);
  font-size: var(--t-min);
}

/* A URL or a 64-character hash has no spaces to break at; without this it widens the drawer. */
.wrap {
  overflow-wrap: anywhere;
}

/* The document behind a figure, as something to follow rather than something to read (EXP-11). */
.link {
  color: var(--accent);
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* A link in the chain that does not reach anything. Not the critical colour: the receipt's own
   `evidence_broken_at` is where the chain's failure is stated, and two red things saying one
   thing reads as two problems. */
.missing {
  color: var(--muted);
  font-style: italic;
}

.arith {
  background: var(--panel-2);
  border: 1px solid var(--line);
  border-radius: 7px;
  padding: 7px 9px;
  font-family: var(--mono);
  font-size: var(--t-min);
  color: var(--ink-2);
  margin-top: 5px;
  line-height: 1.6;
  overflow-x: auto;
}

.licence {
  font-size: var(--t-min);
  color: var(--muted);
  font-style: italic;
  margin-top: 4px;
}

.note {
  font-size: var(--t-min);
  color: var(--ink-2);
  background: var(--panel-2);
  border-left: 2px solid var(--line-2);
  padding: 5px 8px;
  margin-top: 5px;
}

/* The paged breakdown's footer: how many are listed of how many, and the way to more. */
.more {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}

.moreButton {
  align-self: flex-start;
  font: inherit;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--ink);
  background: var(--panel);
  border: 1px solid var(--line-2);
  border-radius: 7px;
  padding: 6px 12px;
  cursor: pointer;
}

.moreButton:hover:not(:disabled) {
  background: var(--panel-2);
}

.moreButton:disabled {
  color: var(--muted);
  cursor: default;
}

.badge {
  display: inline-block;
  font-size: var(--t-min);
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 5px;
  margin-left: 6px;
}

.badge::first-letter {
  text-transform: uppercase;
}

.badgeOk {
  color: var(--accent);
  background: var(--accent-w);
}

.badgeWarn {
  color: var(--warn);
  background: var(--warn-w);
}

/* A restated figure. Loud on purpose: the whole hazard of a receipt that keeps resolving forever
   (ADR-0030) is that a quoted number looks live when it has been replaced. */
.restated {
  font-size: var(--t-min);
  line-height: 1.45;
  color: var(--warn);
  background: var(--warn-w);
  border: 1px solid var(--warn);
  border-radius: 6px;
  padding: 7px 9px;
  margin: 0 0 8px;
}

.restatedLink {
  display: block;
  margin-top: 4px;
  font: inherit;
  color: var(--warn);
  text-decoration: underline;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  text-align: left;
}

/* "Nothing replaced it": the same box as the link, minus the underline of something to press. */
.restatedPlain {
  text-decoration: none;
}

/* The breakdown list, shown when a KPI is a sum rather than a single figure. */
.list {
  display: flex;
  flex-direction: column;
}

.listRow {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 13px;
  border-top: 1px solid var(--line);
  font-size: var(--t-min);
  background: none;
  border-left: 0;
  border-right: 0;
  border-bottom: 0;
  width: 100%;
  text-align: left;
  cursor: pointer;
  color: inherit;
}

.listRow:hover {
  background: var(--panel-2);
}

.listName {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.listSub {
  display: block;
  color: var(--muted);
  font-size: var(--t-min);
}

.listValue {
  font-variant-numeric: lining-nums tabular-nums;
  white-space: nowrap;
}

.chev {
  color: var(--accent);
  flex-shrink: 0;
}

.loading,
.error {
  padding: 18px 13px;
  font-size: var(--t-dense);
  color: var(--muted);
}

.error {
  color: var(--crit);
}

@media (max-width: 900px) {
  .drawer {
    grid-column: 1 / -1;
    grid-row: auto;
    /* **Content height, not a share of what is left.** The base rule's `min-height: 0` and
       `overflow: hidden` make the drawer's automatic minimum size zero, so in the body's
       `grid-auto-rows: auto` the track keeps a base size of nothing and takes only what is spare:
       on a screen whose body scrolls (`main.bodyScrolls`, or any screen with content past the
       fold) there is none, and a receipt was a two-pixel line at the end of the page at 375px
       (measured 2026-10-29 on /emissions and /reports). At this width the page body is the
       scroller, so the drawer simply takes its content's height. */
    min-height: auto;
    overflow: visible;
  }

  .scroll {
    flex: none;
    overflow-y: visible;
  }
}

/* Back to the breakdown a receipt was opened from. Present only when there is one — a receipt
   reached by a shared link has nowhere to go back to. */
.back {
  align-self: flex-start;
  font-size: var(--t-min);
  border: 1px solid var(--line);
  background: var(--panel);
  color: var(--accent);
  border-radius: 6px;
  padding: 3px 8px;
  cursor: pointer;
  margin-bottom: 4px;
}

.back:hover {
  background: var(--accent-w);
}

/* An incomplete chain, and which link is missing (F10). Sits directly under the figure, because a
   reader deciding whether to quote a number should not have to scroll to find out they should not. */
.incomplete {
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--warn);
  background: var(--warn-w);
  border: 1px solid var(--warn-line);
  border-radius: 6px;
  padding: 7px 9px;
  margin: 0 0 8px;
}

.incompleteMet {
  color: var(--ink-2);
}
